// UI strings for a typed form prompt said as one instruction right above the input (exercises/formAsk.ts; Axel
// 2026-10-20, docs/next/fb-form-card-prompts.md): the study card's form drill, an Omat virheet card that asks a form,
// the exercise fill. The language and the target form together ("Kirjoita valyriaksi monikossa"), a verb with its
// pronoun, and the one-time hint when the typed answer is the word's basic form. Finnish (default) and English side by
// side; en has the same shape. Style: docs/FINNISH-STYLE.md (its grammar terms; "monikon genetiivi" as in the lessons,
// the numbers in the inessive as listenForms' numberIn).
import type { Case, GramNumber, Person } from '@valyrio/engine/types';
import type { FormAskKind } from '../../exercises/formAsk';
import { defineArea } from '../area';

/**
 * A command to the one or the many you speak to (lesson "Käsky sinulle ja teille", listenForms' command); `both`: one form
 * that is either, the noun said once ("Kirjoita valyriaksi käsky sinulle tai teille").
 */
const commandFi: Record<'sg' | 'pl' | 'both', string> = { sg: 'käsky sinulle', pl: 'käsky teille', both: 'käsky sinulle tai teille' };
/** The same in the hint, in the essive: "Kirjoita se käskynä sinulle." */
const commandAsFi: Record<'sg' | 'pl' | 'both', string> = { sg: 'käskynä sinulle', pl: 'käskynä teille', both: 'käskynä sinulle tai teille' };

export const formAskFi = {
  /** The instruction around its target words, per kind: [before, after]. */
  frame: {
    number: ['Kirjoita valyriaksi ', ''],
    case: ['Kirjoita valyriaksi ', ''],
    imperative: ['Kirjoita valyriaksi ', ''],
    finite: ['Kirjoita valyriaksi: ', ''],
    other: ['Kirjoita valyriaksi: ', ''],
  } as Record<FormAskKind, readonly [string, string]>,
  /** The nominative's number as the target: "Kirjoita valyriaksi monikossa". */
  number: { sg: 'yksikössä', pl: 'monikossa', pc: 'muutamassa', col: 'kollektiivissa', pcpl: 'muutaman monikossa', colpl: 'kollektiivin monikossa' } as Record<
    GramNumber,
    string
  >,
  /** Another case: "yksikön akkusatiivissa", "monikon genetiivissä". */
  case: {
    nom: 'nominatiivissa',
    acc: 'akkusatiivissa',
    gen: 'genetiivissä',
    dat: 'datiivissa',
    loc: 'lokatiivissa',
    ins: 'instrumentaalissa',
    com: 'komitatiivissa',
    voc: 'vokatiivissa',
  } as Record<Case, string>,
  /** The number before a case or a person: "monikon genetiivissä", "yksikön 3. persoona". */
  numberOf: { sg: 'yksikön', pl: 'monikon', pc: 'muutaman', col: 'kollektiivin', pcpl: 'muutaman monikon', colpl: 'kollektiivin monikon' } as Record<
    GramNumber,
    string
  >,
  caseNumber: (caseWord: string, numberOf: string) => `${numberOf} ${caseWord}`,
  command: commandFi,
  commandAs: commandAsFi,
  /** A finite verb's person: "yksikön 3. persoona"; its pronoun follows in parentheses. */
  person: (p: Person, numberOf: string | null) => (numberOf ? `${numberOf} ${p}. persoona` : `${p}. persoona`),
  pronoun: { sg: { 1: 'minä', 2: 'sinä', 3: 'hän' }, pl: { 1: 'me', 2: 'te', 3: 'he' } } as Record<'sg' | 'pl', Record<Person, string>>,
  /**
   * Between two readings of one verb form, each right: "yksikön 1. persoona (minä) tai yksikön 3. persoona (hän)"; and
   * before the last of a fill's cases or numbers: "yksikön lokatiivissa tai datiivissa".
   */
  or: ' tai ',
  /** The answer typed was the word's basic form (shown once, nothing is graded). */
  hint: (kind: FormAskKind, target: string) =>
    `Tämä on sanan perusmuoto. ${
      kind === 'number' || kind === 'case'
        ? `Kirjoita se ${target}.`
        : kind === 'imperative'
          ? `Kirjoita se ${target === commandFi.both ? commandAsFi.both : target === commandFi.pl ? commandAsFi.pl : commandAsFi.sg}.`
          : `Kirjoita pyydetty muoto: ${target}.`
    }`,
};

export const formAskEn: typeof formAskFi = {
  frame: {
    number: ['Type the ', ' in High Valyrian'],
    case: ['Type the ', ' in High Valyrian'],
    imperative: ['Type ', ' in High Valyrian'],
    finite: ['Type in High Valyrian: ', ''],
    other: ['Type in High Valyrian: ', ''],
  },
  number: { sg: 'singular', pl: 'plural', pc: 'paucal', col: 'collective', pcpl: 'paucal plural', colpl: 'collective plural' },
  case: {
    nom: 'nominative',
    acc: 'accusative',
    gen: 'genitive',
    dat: 'dative',
    loc: 'locative',
    ins: 'instrumental',
    com: 'comitative',
    voc: 'vocative',
  },
  numberOf: { sg: 'singular', pl: 'plural', pc: 'paucal', col: 'collective', pcpl: 'paucal plural', colpl: 'collective plural' },
  caseNumber: (caseWord, numberOf) => `${caseWord} ${numberOf}`,
  command: { sg: 'a command to one person', pl: 'a command to several people', both: 'a command to one person or to several people' },
  commandAs: { sg: 'a command to one person', pl: 'a command to several people', both: 'a command to one person or to several people' },
  person: (p, numberOf) => `${p === 1 ? '1st' : p === 2 ? '2nd' : '3rd'} person${numberOf ? ` ${numberOf}` : ''}`,
  pronoun: { sg: { 1: 'I', 2: 'you', 3: 'he/she' }, pl: { 1: 'we', 2: 'you', 3: 'they' } },
  or: ' or ',
  hint: (kind, target) =>
    `That is the word’s basic form. ${
      kind === 'number' || kind === 'case'
        ? `Type it in the ${target}.`
        : kind === 'imperative'
          ? `Type it as ${target}.`
          : `Type the form asked for: ${target}.`
    }`,
};

// Messages.formAsk (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('formAsk', formAskFi, formAskEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof formAskFi> {}
}
